import React, { useState, useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import ErrorBoundary from "../components/ErrorBoundary.jsx";
import { CAUSE_OPTIONS } from "./tmPricing";

export const CAUSE_PROMPT_TITLE = "What caused this defect?";

/** The defect_reason sent with a picked cause: the typed note, else the cause's own label. */
export function causeReason(causeCode, note) {
  const typed = typeof note === "string" ? note.trim() : "";
  if (typed) return typed;
  const opt = CAUSE_OPTIONS.find((c) => c.code === causeCode);
  return opt ? opt.label : "";
}

/**
 * Imperative defect-cause picker (T&M jobs, Mike's decision 15): a Bootstrap modal with the 7
 * neutral station causes ("Other" allowed) and an optional note. Like pinPrompt it renders in its
 * own React root, inside an ErrorBoundary, above any modal that opened it.
 *
 * Resolves {cause_code, reason} (reason = the note, else the cause's label), or null on Cancel,
 * the header X or Escape. A tap on the backdrop does nothing (backdrop="static").
 * A cause is required: OK stays disabled until one is picked. The wording is neutral on purpose:
 * the cause records what happened; it never decides who pays (that is the office's decision at
 * pricing).
 *
 * Usage:  const pick = await causePrompt({ subject: "FAB311916, 2 pcs" });
 */
export default function causePrompt({ subject = "" } = {}) {
  return new Promise((resolve) => {
    const container = document.createElement("div");
    document.body.appendChild(container);
    const root = createRoot(container);
    let settled = false;

    function finish(value) {
      if (settled) return;
      settled = true;
      root.unmount();
      container.remove();
      resolve(value);
    }

    // A crash inside the picker must still settle the caller's promise (as a cancel).
    root.render(
      <ErrorBoundary
        fallback={(error) => (
          <div className="alert alert-danger m-3" role="alert" style={{ position: "fixed", zIndex: 2001, top: 0, left: 0, right: 0 }}>
            The defect cause picker hit an error: {(error && error.message) || String(error)}. Nothing was saved.
            <button type="button" className="btn btn-sm btn-outline-danger ms-2" onClick={() => finish(null)}>Close</button>
          </div>
        )}
      >
        <CauseModal subject={subject} onConfirm={(v) => finish(v)} onCancel={() => finish(null)} />
      </ErrorBoundary>
    );
  });
}

function CauseModal({ subject, onConfirm, onCancel }) {
  const [show, setShow] = useState(true);
  const [cause, setCause] = useState("");
  const [note, setNote] = useState("");
  const firstRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (firstRef.current) firstRef.current.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  function handleSubmit(e) {
    e.preventDefault();
    if (!cause) return;
    setShow(false);
    onConfirm({ cause_code: cause, reason: causeReason(cause, note) });
  }

  function handleClose() {
    setShow(false);
    onCancel();
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      // A stray tap outside the dialog on a shop touchscreen must not drop the picked cause:
      // only Cancel, the header X or Escape close it.
      backdrop="static"
      centered
      style={{ zIndex: 2000 }}
      backdropClassName="causePromptBackdrop"
      aria-labelledby="cause-prompt-title"
    >
      <style>{`.causePromptBackdrop { z-index: 1999 !important; }`}</style>
      <Modal.Header closeButton>
        <Modal.Title id="cause-prompt-title" style={{ fontSize: "1rem" }}>{CAUSE_PROMPT_TITLE}</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          {subject && <div className="small text-muted mb-2">{subject}</div>}
          <fieldset>
            <legend className="form-label mb-1" style={{ fontSize: "0.9rem" }}>
              Pick the cause that fits best (required). &quot;Other&quot; is fine when none fits.
            </legend>
            {CAUSE_OPTIONS.map((c, i) => (
              <div className="form-check" key={c.code}>
                <input
                  ref={i === 0 ? firstRef : undefined}
                  className="form-check-input"
                  type="radio"
                  name="defect-cause"
                  id={`defect-cause-${c.code}`}
                  value={c.code}
                  checked={cause === c.code}
                  onChange={() => setCause(c.code)}
                />
                <label className="form-check-label" htmlFor={`defect-cause-${c.code}`}>{c.label}</label>
              </div>
            ))}
          </fieldset>
          <label className="form-label mt-2 mb-1" htmlFor="defect-cause-note">Note (optional)</label>
          <input
            id="defect-cause-note"
            type="text"
            className="form-control"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            autoComplete="off"
            placeholder="What happened?"
            maxLength={500}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" type="submit" disabled={!cause}>
            OK
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
